import { BaseText, Headline1, ListItem, Meta, theme } from 'aimo-ui';
import {
  CardBackground,
  CutWrapper,
  LeftCut,
  RightCut,
} from 'components/common/CrickleCutCardBackground';
import PromotionInfo from 'components/common/PromotionInfo';
import Icon from 'components/icons/Icon';
import SkeletonLoaderRow, {
  SkeletonWrapper,
} from 'components/loading/SkeletonLoaderRow';
import ValidFromPicker from 'components/longTermParking/ValidFromPicker';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParkzoneStore } from 'stores/parkzoneStore';
import {
  CenterFullScreen,
  ColumnWithMargins,
  StyledImage,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  BillingFrequency,
  CountryCode,
  CustomerPmcPromotion,
  Maybe,
} from 'types/generatedSchemaTypes';
import { formatPriceForBillingFrequency, PmcPrice } from 'utils/commonUtils';
import { getPromotionPricing } from 'utils/promotionUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import AttachmentCardRow from './AttachmentCardRow';
import BillingInterval from './BillingInterval';
import { BlackDot, ListItemWrapper, Row } from './styles';

type parkingInfo = {
  parkingZoneName: string;
  location?: string;
  permitName: string;
  price: PmcPrice;
  validFrom?: Date;
  validUntil?: string;
  billingType?: string;
  changeValid?: (newDate: Date) => void;
  testID?: string;
  isCityPmc?: boolean;
  isReserved?: boolean;
  enableDateTimePicker?: boolean;
  onParkingSpacePress?: () => void;
  onlineBookingFlexibleStartDate: number;
  startSellingDate?: Maybe<Date>;
  showAttachment?: boolean;
  promotion?: Maybe<CustomerPmcPromotion>;
  promotionLoading?: boolean;
  country?: Maybe<CountryCode>;
  billingFrequency?: BillingFrequency;
};

const RightContent = ({
  text,
  testID,
  textColor,
}: {
  text: string;
  testID?: string;
  textColor?: string;
}) => (
  <Row>
    <Meta color={textColor} {...testIdentifiers(`${testID}-content`)}>
      {text}
    </Meta>
    <Icon color={textColor} testID={`${testID}-icon`} name="Chevron_Right" />
  </Row>
);

const SelectListItem = ({
  title,
  selection,
  onPress,
  testID,
  noBorder = false,
  textColor,
}: {
  title: string;
  selection: string;
  onPress?: () => void;
  testID: string;
  noBorder?: boolean;
  textColor?: string;
}) => (
  <ListItemWrapper noBorder={noBorder}>
    <ListItem
      testID={testID}
      onPress={onPress}
      withBackground={theme.colors.lightBg.green}
      item={null}
      indexValue={0}
      titleComponent={() => (
        <Meta
          testID={`${testID}-title`}
          color={textColor ?? theme.colors.gray300}>
          {title}
        </Meta>
      )}
      rightContentComponent={() => (
        <RightContent textColor={textColor} testID={testID} text={selection} />
      )}
    />
  </ListItemWrapper>
);

export const ChangeSpaceItem = ({
  testID,
  onPress,
  parkingSpaceName,
}: {
  testID: string | undefined;
  onPress?: () => void;
  parkingSpaceName?: string;
}) => {
  const { t } = useTranslation();
  const parkingSpace = useParkzoneStore((state) => state.selectedParkingSpace);
  return (
    <SelectListItem
      title={t('longTermParking.parkingSpace')}
      selection={
        parkingSpaceName ??
        parkingSpace?.name ??
        t('longTermParking.selectSpace')
      }
      onPress={onPress}
      textColor={
        typeof onPress === 'function' ? undefined : theme.colors.gray300
      }
      testID={`${testID}-space-selection`}
    />
  );
};

const TitleCard = ({
  testID,
  parkingZoneName,
  permitName,
  price,
  validFrom,
  changeValid,
  isCityPmc = false,
  validUntil,
  location,
  isReserved,
  enableDateTimePicker,
  onParkingSpacePress,
  onlineBookingFlexibleStartDate,
  startSellingDate,
  showAttachment = false,
  promotion,
  promotionLoading = false,
  country,
  billingFrequency = 'MONTHLY',
}: parkingInfo) => {
  const { t } = useTranslation();
  const { totalPrice, serviceFee, totalPriceCents, serviceFeeCents } = price;
  const shouldDisplayValidFromPicker =
    enableDateTimePicker && validFrom && changeValid;

  const {
    promotionLike,
    hasActivePromotion,
    originalPriceValue,
    discountedPriceValue,
  } = getPromotionPricing(
    promotion,
    totalPriceCents,
    serviceFeeCents,
    validFrom ?? new Date()
  );

  const headlinePrice =
    discountedPriceValue !== undefined && country
      ? formatPriceForBillingFrequency(
          discountedPriceValue,
          country,
          billingFrequency
        )
      : totalPrice;

  return (
    <CardBackground>
      <StyledWrapper mb={theme.normalising.heightPixel(27)}>
        <StyledImage
          center={true}
          width={114}
          height={98}
          source={require('assets/images/permit.png')}
          testID={`${testID}-image`}
        />
      </StyledWrapper>
      <StyledWrapper center={true} mb={theme.normalising.heightPixel(25)}>
        <Headline1 center={true} testID={`${testID}-title`}>
          {!isCityPmc ? t('longTermParking.title') : t('cityPermits.title')}
        </Headline1>
      </StyledWrapper>
      <StyledWrapper center={true}>
        <BaseText center {...testIdentifiers(`${testID}-garage-name`)}>
          {parkingZoneName}
        </BaseText>
        <Row>
          <BaseText color={theme.colors.gray300} testID={`${testID}-location`}>
            {location}
          </BaseText>
          {location && <BlackDot />}
          <BaseText center testID={`${testID}-permit-name`}>
            {permitName}
          </BaseText>
        </Row>
        <ColumnWithMargins mt={theme.normalising.heightPixel(20)}>
          <CenterFullScreen>
            {promotionLoading ? (
              <StyledWrapper center mb={0} testID={`${testID}-price-loading`}>
                <SkeletonWrapper width={140}>
                  <SkeletonLoaderRow height={34} />
                </SkeletonWrapper>
              </StyledWrapper>
            ) : (
              <Headline1 center testID={`${testID}-price`}>
                {headlinePrice}
              </Headline1>
            )}
            {serviceFee && (
              <ColumnWithMargins mt={5}>
                <Meta color={theme.colors.gray300}>{serviceFee}</Meta>
              </ColumnWithMargins>
            )}
            {hasActivePromotion && (
              <PromotionInfo
                promotion={promotionLike}
                country={country}
                testID={testID}
                originalPrice={originalPriceValue}
                discountedPrice={discountedPriceValue}
              />
            )}
          </CenterFullScreen>
        </ColumnWithMargins>
      </StyledWrapper>
      <CutWrapper>
        <LeftCut />
        <RightCut />
      </CutWrapper>
      <StyledWrapper center={true} maxWidth={320} mb={0}>
        {validFrom && (
          <ValidFromPicker
            disabled={!shouldDisplayValidFromPicker}
            selectedDate={validFrom}
            validFrom={validFrom}
            onConfirm={(newDate) => changeValid?.(newDate)}
            testID={`${testID}-valid-from-selection`}
            minDaysBeforeStart={onlineBookingFlexibleStartDate}
            minimumDate={
              startSellingDate ? new Date(startSellingDate) : undefined
            }
            maxDaysBeforeStart={60}
          />
        )}
        {validUntil && (
          <StyledWrapper mb={theme.normalising.heightPixel(20)}>
            <Row justify="space-between">
              <BaseText testID={`${testID}-valid-until`}>
                {t('cityPermits.validUntil')}
              </BaseText>
              <BaseText testID={`${testID}-until-date`}>{validUntil}</BaseText>
            </Row>
          </StyledWrapper>
        )}
        <BillingInterval noBorder={!validFrom} />
        {isReserved && (
          <ChangeSpaceItem onPress={onParkingSpacePress} testID={testID} />
        )}
        {showAttachment && <AttachmentCardRow />}
      </StyledWrapper>
    </CardBackground>
  );
};

export default TitleCard;
